// * [WARNING] https://reactjs.org/docs/hooks-rules.html
//@ts-check
import { useState, useEffect, useRef, EffectCallback, DependencyList, useLayoutEffect, useCallback } from 'react';
import { isFunction, debounce, isArray, without } from 'lodash-es';
import { useSearchParams, URLSearchParamsInit } from 'react-router-dom';
import { useEventListener, useIsomorphicLayoutEffect, useIsMounted } from 'usehooks-ts';
import { getLocationInfo, isInGeotarget } from '../../utils/location';
import { ShippingCountryState } from '../../utils/shipping';
import { isTagPresent, poll } from '../../utils/helpers';
import { EXCLUSION_TAG } from '../../utils/constants';

// * [IMPROVEMENT] add an options {} for customization; use real Typescript for access to generics
// ? [REVISIT] this isn't the right pattern for Errors
export const usePromise = <T, E extends Error>(
  //@ts-ignore
  promise: Promise<T> | (() => Promise<T>),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dependencies: any[] = [],
  {
    // ? [REVISIT] this should have been default `true`, but can't risk breaking other instance of `usePromise`
    // - intentionally kept out of the depencecy array (at least for now)
    loadingTrueWhenRefetching = false,
  } = {}
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<T>(); // * [IMPROVEMENT] if we had real typescript `useState<T | null>()`
  const [error, setError] = useState<E>();
  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      try {
        if (loadingTrueWhenRefetching) setIsLoading(true);
        const promiseToResolve = isFunction(promise) ? promise() : promise;
        const locationInfo = await promiseToResolve;
        if (isMounted) setData(locationInfo);
      } catch (err) {
        console.error(err);
        if (isMounted) setError(err as E);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
    // ? [REVISIT] take inspiration from ReactQuery or something similar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return { data, isLoading, error };
};

// ? [REVISIT] how to integrate w/ rule of hooks 'exhaustive dependency' rule
export const useDidUpdateEffect = (effect: EffectCallback, deps?: DependencyList, runOnFirstRun = false) => {
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current || runOnFirstRun) effect();
    else didMountRef.current = true;
    // ? [REVISIT]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

// ? [REVISIT] should `useDidUpdateEffect` use `useIsFirstRender`, 2 effects vs 1? should it just return firstRender?
export const useIsFirstRender = () => {
  const didMountRef = useRef(false);
  useEffect(() => {
    didMountRef.current = true;
  }, []);
  return !didMountRef.current;
};

export function useWindowSize() {
  function getSize() {
    return {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    };
  }
  const [windowSize, setWindowSize] = useState(getSize);
  useEffect(() => {
    let isMounted = true;
    const handleResize = () => {
      if (isMounted) setWindowSize(getSize());
    };
    const debouncedHandleResize = debounce(handleResize, 150);
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      isMounted = false;
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  return windowSize;
}

type NavigationOptions =
  | {
      replace?: boolean | undefined;
      state?: object | null | undefined;
    }
  | undefined;
// ? [REVISIT] rename / better name; the helper functions can be optimized
export const useSearchParamHelpers = (
  defaultInit?: URLSearchParamsInit,
  defaultNavigateOptions?: NavigationOptions
) => {
  const [searchParams, _setSearchParams] = useSearchParams(defaultInit);

  const setSearchParams = (nextInit: URLSearchParamsInit, navigateOptions = defaultNavigateOptions) =>
    _setSearchParams(nextInit, navigateOptions);

  // Appends the value to the existing key (comma separated). Creates new key-value pair if key doesn't exist
  const setNewValue = (key: string, values: string | string[], navigateOptions = defaultNavigateOptions) => {
    const newValuesArr = isArray(values) ? values : [values];
    const copy = new URLSearchParams(searchParams);
    copy.delete(key);
    if (newValuesArr.length) copy.set(key, newValuesArr.join());
    setSearchParams(copy, navigateOptions);
  };

  //  If key is exists adds the value with comma to the key. If not exist creates a new key with value
  const toggleValue = (key: string, valueName: string, navigateOptions = defaultNavigateOptions) => {
    let optionsCurrentValues = searchParams.getAll(key);
    if (optionsCurrentValues.length > 0) optionsCurrentValues = optionsCurrentValues.join().split(',');
    const isChecked = optionsCurrentValues.includes(valueName);
    const newValues = isChecked ? without(optionsCurrentValues, valueName) : [...optionsCurrentValues, valueName];
    setNewValue(key, newValues, navigateOptions);
  };

  const toggleValues = (key: string, valueNames: string[], navigateOptions = defaultNavigateOptions) => {
    let optionsCurrentValues = searchParams.getAll(key);
    if (optionsCurrentValues.length > 0) optionsCurrentValues = optionsCurrentValues.join().split(',');

    const newValues = valueNames.reduce((acc, valueName) => {
      const isChecked = optionsCurrentValues.includes(valueName);
      return isChecked ? without(acc, valueName) : [...acc, valueName];
    }, optionsCurrentValues);

    setNewValue(key, newValues, navigateOptions);
  };

  return { searchParams, setSearchParams, setNewValue, toggleValue, toggleValues };
};

export function ScrollToTopOnMount() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return null;
}

export const useDimensions = (shouldPoll = false) => {
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  //@ts-ignore
  const [dimensions, setDimensions]: [
    ClientRect | DOMRect | undefined,
    React.Dispatch<React.SetStateAction<ClientRect | DOMRect | undefined>>
  ] = useState();
  useLayoutEffect(() => {
    let isMounted = true;
    function handleResize() {
      if (ref.current && isMounted) {
        setDimensions(ref.current.getBoundingClientRect());
      }
    }
    const handleResizeDebounced = debounce(handleResize, 100, { trailing: true });
    handleResize();
    // ! [REVISIT] perf; resize observer?
    if (shouldPoll) window.addEventListener('resize', handleResizeDebounced);
    return () => {
      isMounted = false;
      if (shouldPoll) window.removeEventListener('resize', handleResizeDebounced);
    };
  }, [shouldPoll]);

  const returnValue: [React.MutableRefObject<HTMLDivElement | null>, ClientRect | DOMRect | undefined] = [
    ref,
    dimensions,
  ];
  return returnValue;
};

export const useForceUpdate = () => {
  const [, forceUpdate] = useState(false);
  return useCallback(() => {
    forceUpdate((s) => !s);
  }, []);
};

type ResizeObserverSize = {
  width: number | undefined;
  height: number | undefined;
};

const ResizeObserverInitialSize: ResizeObserverSize = {
  width: undefined,
  height: undefined,
};

export const useResizeObserver = () => {
  const isMounted = useIsMounted();
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  const previousSize = useRef<ResizeObserverSize>({ ...ResizeObserverInitialSize });
  const [{ width, height }, setSize] = useState<ResizeObserverSize>(ResizeObserverInitialSize);

  useEffect(() => {
    if (!ref.current) return;
    let observer = new ResizeObserver(() => {
      if (ref.current) {
        const { height: newHeight, width: newWidth } = ref.current.getBoundingClientRect();
        const hasChanged = previousSize.current.width !== newWidth || previousSize.current.height !== newHeight;
        if (hasChanged && isMounted()) {
          setSize({ height: newHeight, width: newWidth });
        }
      }
    });
    observer.observe(ref.current);
    return () => {
      observer?.disconnect();
    };
  }, [ref, isMounted]);

  const returnValue: [React.MutableRefObject<HTMLDivElement | null>, ResizeObserverSize] = [ref, { width, height }];
  return returnValue;
};

// Returns an Entry {} | undefined. For more details see: https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserverEntry
export const useIntersectionObserver = (
  ref: React.MutableRefObject<HTMLElement | null>,
  options?: IntersectionObserverInit | undefined
) => {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const element = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry) {
        setEntry(entry);
        setIsIntersecting(entry.isIntersecting);
      }
    }, options);
    if (element) {
      observer.observe(element);
    }
    return () => {
      if (element) observer.unobserve(element);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return { entry, isIntersecting };
};

// Get dynamic element's dimensions
export const useElementDimensions = (selector: string) => {
  const [dimensions, setDimensions] = useState({
    height: 0,
    width: 0,
  });
  useLayoutEffect(() => {
    const setElementDimensions = () => {
      const element = document.querySelector(selector);
      if (element) {
        const { height, width } = element.getBoundingClientRect();
        setDimensions({ height, width });
      }
    };
    const debouncedSetElementDimensions = debounce(setElementDimensions, 150);
    window.addEventListener('resize', debouncedSetElementDimensions);
    setElementDimensions();
  }, [selector]);
  return dimensions;
};

interface Size {
  width: number;
  height: number;
  scrollWidth: number;
}

export function useElementSize<T extends HTMLElement = HTMLDivElement>(): [(node: T | null) => void, Size] {
  // Mutable values like 'ref.current' aren't valid dependencies
  // because mutating them doesn't re-render the component.
  // Instead, we use a state as a ref to be reactive.
  const [ref, setRef] = useState<T | null>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
    scrollWidth: 0,
  });

  // Prevent too many rendering using useCallback
  const handleSize = useCallback(() => {
    setSize({
      width: ref?.offsetWidth || 0,
      height: ref?.offsetHeight || 0,
      scrollWidth: ref?.scrollWidth || 0,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.offsetHeight, ref?.offsetWidth]);

  useEventListener('resize', handleSize);

  useIsomorphicLayoutEffect(() => {
    handleSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.offsetHeight, ref?.offsetWidth]);

  return [setRef, size];
}

export default useElementSize;

export function usePrevious<T>(value: T): T {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref: any = useRef<T>();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

// check if user is in the US
export const useCheckIfUserIsInUS = () => {
  const [userIsInUS, setUserIsInUS] = useState(() => ShippingCountryState.countryCode === 'US');

  useEffect(() => {
    const checkIfUserIsInUS = async () => {
      const userCountryCode = ShippingCountryState.countryCode;
      if (userCountryCode) {
        setUserIsInUS(userCountryCode === 'US');
        return;
      }

      const userIsInUSGeo = await isInGeotarget({ countries: ['US'] });
      setUserIsInUS(userIsInUSGeo);
    };
    checkIfUserIsInUS();
  }, []);

  return userIsInUS;
};

// Get country code by priority; User preferred country is higher priority than IP location
export const useCountryCode = () => {
  const [countryCode, setCountryCode] = useState('US');

  useEffect(() => {
    const getCurrentCountryCode = async () => {
      const geoLocationInfo = await getLocationInfo();
      const userPreferredCountryCode = ShippingCountryState.countryCode;
      const priorityCountryCode = userPreferredCountryCode ? userPreferredCountryCode : geoLocationInfo.country_code;
      setCountryCode(priorityCountryCode);
    };
    getCurrentCountryCode();
  }, []);

  return countryCode;
};

// Custom hook that returns Loop Exchange flow status and checks if product tags exclude it from exchange
export const useLoopExchangeStatusForProduct = (tags: string[]) => {
  const [isExchangeFlow, setIsExchangeFlow] = useState(false);
  const isProductExcludedFromExchange = isTagPresent(tags, EXCLUSION_TAG);

  useEffect(() => {
    poll(() => window.LoopOnstore?.isActive, 5000, 1000)
      .then(() => {
        const isLoopActive = window.LoopOnstore.isActive();
        setIsExchangeFlow(isLoopActive);
      })
      .catch((error) => {
        console.info(error);
      });
  }, []);

  return {
    isExchangeFlow,
    isProductExcludedFromExchange,
  };
};

// this hook manages focus restoration when a component (like a modal or sidebar) is opened or closed
export function useRestoreFocusOnOpen(isOpen: boolean, focusRef: React.RefObject<HTMLElement>) {
  const prevFocusedElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      prevFocusedElement.current = document.activeElement as HTMLElement;
      focusRef.current?.focus();
    } else {
      prevFocusedElement.current?.focus();
    }
  }, [isOpen, focusRef]);
}
